import { useMemo } from 'react'

import type { ToolbarTaskItem } from '#/shared/api/generated/shipment/models'
import { useListItemsApiV1ToolbarTypeGet } from '#/shared/api/generated/shipment/theCargoShipment'
import { formatNoteTimestamp } from '#/shared/lib/format'

const DEFAULT_TASK_FEED_SIZE = 100

export interface ShipmentTaskFeedItem {
  id: string
  title: string
  type: string
  priority: string
  status: string
  date: string | undefined
  startTime: string | undefined
  endTime: string | undefined
  timestamp: string
  occurredAt: string
  author: string | undefined
  assignees: string[]
}

interface UseShipmentTaskFeedOptions {
  enabled?: boolean
  size?: number
}

export interface UseShipmentTaskFeedResult {
  tasks: ShipmentTaskFeedItem[]
  isLoading: boolean
}

function formatActorName(
  actor: { first_name?: string | null; last_name?: string | null } | undefined,
): string | undefined {
  if (!actor) return undefined
  const name = [actor.first_name, actor.last_name]
    .filter(Boolean)
    .join(' ')
    .trim()
  return name.length > 0 ? name : undefined
}

export function toShipmentTaskFeedItem(
  task: ToolbarTaskItem,
): ShipmentTaskFeedItem {
  return {
    id: task.id,
    title: task.title,
    type: task.type,
    priority: task.priority,
    status: task.status,
    date: task.date ?? undefined,
    startTime: task.start_time ?? undefined,
    endTime: task.end_time ?? undefined,
    timestamp: formatNoteTimestamp(task.created_at),
    occurredAt: task.created_at,
    author: formatActorName(task.user ?? undefined),
    assignees: (task.assignees ?? [])
      .map(formatActorName)
      .filter((name): name is string => Boolean(name)),
  }
}

export function useShipmentTaskFeed(
  shipmentId: string,
  {
    enabled = true,
    size = DEFAULT_TASK_FEED_SIZE,
  }: UseShipmentTaskFeedOptions = {},
): UseShipmentTaskFeedResult {
  const query = useListItemsApiV1ToolbarTypeGet(
    'task',
    { shipment_id: shipmentId, size },
    { query: { enabled: enabled && Boolean(shipmentId) } },
  )

  const tasks = useMemo<ShipmentTaskFeedItem[]>(() => {
    const items = (query.data?.data.items ?? []) as unknown as ToolbarTaskItem[]
    return items.map(toShipmentTaskFeedItem)
  }, [query.data])

  return {
    tasks,
    isLoading: query.isLoading && enabled && Boolean(shipmentId),
  }
}
